import { Instagram, MessageCircle, FileText, Star, Lock } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import { SITE, IMAGES, FAQS, waGeneral } from "@/config/siteConfig";
import { REVIEWS } from "@/config/reviews";
import { useQuote } from "@/context/QuoteContext";
import { trackEvent } from "@/lib/analytics";

const Stars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < rating ? "fill-[#D99B26] text-[#D99B26]" : "text-slate-300"}`} />
    ))}
  </div>
);

export const Testimonials = () => (
  <section className="py-16 sm:py-24 bg-white" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Traveller Stories" title="What Our Travellers Say"
        subtitle="Honest words from travellers who planned their Kashmir with us." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {REVIEWS.map((r, i) => (
          <Reveal key={r.name} delay={i * 0.1}>
            <figure className="flex h-full flex-col overflow-hidden rounded-xl bg-[#FAF7F2] ring-1 ring-[#0F2F24]/8 shadow-sm" data-testid={`testimonial-card-${i + 1}`}>
              {r.image && (
                <div className="relative">
                  <img src={r.image} alt={`${r.name} on their Kashmir trip`} loading="lazy"
                    className="aspect-[16/10] w-full object-cover" />
                  <span className="absolute left-3 top-3 rounded-full bg-[#0F2F24]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#D99B26] backdrop-blur-sm">
                    Real Traveller Photo
                  </span>
                </div>
              )}
              <div className="flex flex-1 flex-col p-6">
                <Stars rating={r.rating} />
                <blockquote className="mt-3 flex-1 font-serif text-base italic text-[#1E2922] leading-relaxed">
                  “{r.text}”
                </blockquote>
                <figcaption className="mt-5 border-t border-[#0F2F24]/10 pt-4">
                  <p className="text-sm font-bold text-[#0F2F24]">
                    <span className="blur-[5px] select-none" title="Hidden for traveller privacy">
                      {r.name} {r.masked}{r.suffix ? ` ${r.suffix}` : ""}
                    </span>
                  </p>
                  <p className="mt-0.5 text-xs font-mono uppercase tracking-wider text-[#C28519]">{r.trip}</p>
                </figcaption>
              </div>
            </figure>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 flex items-center justify-center gap-1.5 text-xs text-slate-500" data-testid="reviews-privacy-note">
        <Lock className="h-3.5 w-3.5 text-[#C28519]" /> Traveller names are hidden to protect their privacy.
      </p>
    </div>
  </section>
);

const GRID_IMAGES = [IMAGES.shikara, IMAGES.gulmargSnow, IMAGES.pahalgam, IMAGES.houseboat, IMAGES.sonamarg, IMAGES.meadow];

export const InstagramSection = () => (
  <section className="py-16 sm:py-24" data-testid="instagram-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="On Instagram" title="Follow Our Kashmir Journeys"
        subtitle="Real moments from the valley — follow along for inspiration." />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {GRID_IMAGES.map((src, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer"
              className="group relative block aspect-square overflow-hidden rounded-lg" data-testid={`instagram-tile-${i + 1}`}>
              <img src={src} alt={`Kashmir journey photo ${i + 1}`} loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <span className="absolute inset-0 flex items-center justify-center bg-[#0F2F24]/0 transition-colors group-hover:bg-[#0F2F24]/40">
                <Instagram className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Button asChild variant="outline" className="rounded-full px-6 border-[#0F2F24] text-[#0F2F24] hover:bg-[#0F2F24] hover:text-white">
          <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-button">
            <Instagram className="mr-2 h-4 w-4" /> Follow Us on Instagram {SITE.instagramHandle}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

export const FAQSection = ({ faqs = FAQS, title = "Kashmir Holiday FAQs" }) => (
  <section className="py-16 sm:py-24 bg-white" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Good to Know" title={title} />
      <Reveal>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-accordion-item-${i + 1}`}>
              <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-[#0F2F24] hover:text-[#C28519]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm sm:text-base text-slate-600 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => {
  const { openQuote } = useQuote();
  return (
    <section className="relative overflow-hidden py-20 sm:py-28" data-testid="final-cta-section">
      <div className="absolute inset-0">
        <img src={IMAGES.sonamarg} alt="Kashmir Himalayan mountains" loading="lazy" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0F2F24]/80" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 36 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white text-balance">
          Ready to Experience Kashmir?
        </h2>
        <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed">
          Tell us your travel dates and we'll help you plan a Kashmir holiday that fits your preferences and budget.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <motion.span whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Button onClick={() => openQuote()} data-testid="final-cta-quote-button"
              className="bg-[#D99B26] text-[#0F2F24] hover:bg-[#c98f22] font-semibold rounded-full px-8 h-12 text-base">
              <FileText className="mr-2 h-5 w-5" /> Get My Kashmir Quote
            </Button>
          </motion.span>
          <motion.span whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Button asChild variant="outline" className="rounded-full px-8 h-12 text-base border-white/60 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm">
              <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="final-cta-whatsapp-button"
                onClick={() => trackEvent("whatsapp_click", { location: "final_cta" })}>
                <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp Us
              </a>
            </Button>
          </motion.span>
        </div>
      </motion.div>
    </section>
  );
};
